import { useMemo } from 'react'

import {
  SanityFilterGroup,
  SanitySortOptionType,
} from '@data/sanity/queries/types/filter'
import { SanityProductFragment } from '@data/sanity/queries/types/product'
import { compareNumbers, compareStrings, getAllCombinations } from './helpers'
import { Filter } from './product'
import { UrlParameter } from './url'

/**
 * Returns a method that compares products for sorting by price.
 */
const sortProductsByPrice = (isDescending = false) => {
  const sign = isDescending ? -1 : 1

  return (
    { price: price1 }: SanityProductFragment,
    { price: price2 }: SanityProductFragment,
  ) => sign * compareNumbers(price1, price2)
}

/**
 * Returns a method that compares products for sorting by title.
 */
const sortProductsByTitle = (isDescending = false) => {
  const sign = isDescending ? -1 : 1

  return (
    { title: title1 }: SanityProductFragment,
    { title: title2 }: SanityProductFragment,
  ) => sign * compareStrings(title1, title2)
}

/**
 * Returns a method that compares products for sorting by creation date.
 */
const sortProductsByDate = (isDescending = false) => {
  const sign = isDescending ? -1 : 1

  return (
    { createdAt: createdAt1 }: SanityProductFragment,
    { createdAt: createdAt2 }: SanityProductFragment,
  ) => sign * compareStrings(createdAt1, createdAt2)
}

/**
 * Collection filter and sort hook.
 */
export const useFilterAndSort = (
  products: SanityProductFragment[],
  featuredProductIds: number[],
  filters: Filter[],
  sortValue: SanitySortOptionType | null,
) => {
  const filteredProducts = useMemo(() => {
    // Get all combinations of all filter values
    const filterCombinations = getAllCombinations(
      ...filters
        .filter(({ values }) => values.length > 0)
        .map(({ values }) => values),
    )

    return products.filter((product) => {
      const productFilterValues = product.filters?.map(({ slug }) => slug) ?? []

      return filterCombinations.some((values) =>
        values.every((value) => productFilterValues.includes(value)),
      )
    })
  }, [filters, products])

  switch (sortValue) {
    case SanitySortOptionType.PRICE_ASCENDING: {
      return filteredProducts.sort(sortProductsByPrice())
    }

    case SanitySortOptionType.PRICE_DESCENDING: {
      return filteredProducts.sort(sortProductsByPrice(true))
    }

    case SanitySortOptionType.ALPHA_ASCENDING: {
      return filteredProducts.sort(sortProductsByTitle())
    }

    case SanitySortOptionType.ALPHA_DESCENDING: {
      return filteredProducts.sort(sortProductsByTitle(true))
    }

    case SanitySortOptionType.DATE_ASCENDING: {
      return filteredProducts.sort(sortProductsByDate())
    }

    case SanitySortOptionType.DATE_DESCENDING: {
      return filteredProducts.sort(sortProductsByDate(true))
    }

    case SanitySortOptionType.FEATURED: {
      const productIndices = featuredProductIds.reduce(
        (result: Record<number, number>, value: number, index: number) => (
          (result[value] = index + 1),
          result
        ),
        {},
      )

      return filteredProducts.sort(
        (product1: SanityProductFragment, product2: SanityProductFragment) =>
          (productIndices[product1.id] ?? Infinity) -
          (productIndices[product2.id] ?? Infinity),
      )
    }

    default: {
      return filteredProducts
    }
  }
}

/**
 * Gets sort parameter value from URL parameters.
 */
export const getSortParameterValue = (activeParameters: UrlParameter[]) => {
  const sortParameter = activeParameters.find(({ name }) => name === 'sort')
  const value = sortParameter?.value

  if (!value || Array.isArray(value)) {
    return null
  }

  return value as SanitySortOptionType
}

/**
 * Returns a method that gets filter from a URL parameter.
 */
export const getFilterFromUrlParameter =
  (filterGroups: SanityFilterGroup[]) =>
  (activeParameter: UrlParameter): Filter => {
    const filterGroup = filterGroups.find(
      ({ slug }) => slug === activeParameter.name,
    )
    const optionSlugs = filterGroup?.options?.map(({ slug }) => slug) ?? []
    const values =
      activeParameter.value && Array.isArray(activeParameter.value)
        ? activeParameter.value
        : (activeParameter.value?.split(',') ?? [])

    return {
      name: activeParameter.name,
      values: [
        ...new Set(
          values.filter((value) => value && optionSlugs.includes(value)),
        ),
      ],
    }
  }

/**
 * Returns a method that gets updated URL parameter from given new parameters.
 */
export const getUpdatedParameter = (
  newParameters: UrlParameter[],
  parameter: UrlParameter,
): UrlParameter => {
  const matchedParameter = newParameters.find(
    ({ name }) => name === parameter.name,
  )

  if (!matchedParameter) {
    return parameter
  }

  return {
    ...parameter,
    value: matchedParameter?.value ?? null,
  }
}
